import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import ShareButtons from '../components/ShareButtons';
import { ARTICLE_API, articleImg, articleDate, trunc, stripHtml, SCHOLAR_COLORS, scholarInitial } from '../utils/api';

function Skel({ cls, style }) { return <div className={`skel ${cls||''}`} style={style} />; }

const NO_CONTENT_MSG = {
  ar: 'لا توجد مقالات بهذه اللغة حالياً',
  en: 'No articles available in this language yet.',
  ur: 'اس زبان میں ابھی کوئی مضمون دستیاب نہیں ہے۔',
};

function resolveVersion(a, lang) {
  const map = a.translations_map || a.versions || {};
  if (map[lang] && map[lang].title) {
    return { title: map[lang].title, content: map[lang].content || a.content || '' };
  }
  if (a.language === lang && a.title) {
    return { title: a.title, content: a.content || '' };
  }
  if (map['ar'] && map['ar'].title) {
    return { title: map['ar'].title, content: map['ar'].content || a.content || '' };
  }
  return { title: a.title || '', content: a.content || '' };
}

function hasLang(a, lang) {
  if (lang === 'ar') return true;
  if (a.language === lang) return true;
  const map = a.translations_map || a.versions || {};
  return !!(map[lang] && map[lang].title);
}

export function ArticlesPage({ lang }) {
  const { t } = useLang();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [loadingFirst, setLoadingFirst] = useState(true);

  // Fetch ALL articles whenever the language changes
  useEffect(() => {
    let cancelled = false;
    setLoadingFirst(true);

    fetch(`${ARTICLE_API}?page=1&language=${lang}&per_page=500`)
      .then(r => r.json())
      .then(raw => {
        if (cancelled) return;
        const items = (Array.isArray(raw) ? raw : (raw?.data || []))
          .filter(a => hasLang(a, lang))
          .map(a => ({ ...a, ...resolveVersion(a, lang) }));
        setArticles(items);
      })
      .catch(() => { if (!cancelled) setArticles([]); })
      .finally(() => { if (!cancelled) setLoadingFirst(false); });

    return () => { cancelled = true; };
  }, [lang]);

  return (
    <div id="page-articles">
      <div className="page-hero">
        <div className="geo-lg" /><div className="page-hero-glow" />
        <div className="pw">
          <div className="breadcrumb">
            <a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>الرئيسية</a>
            <span>/</span><span>المقالات</span>
          </div>
          <h1>{t('art.title1')} <em>{t('art.title2')}</em></h1>
          <p>{t('art.subtitle')}</p>
        </div>
      </div>
      <div className="pw">
        <div style={{ padding: '1.5rem 0' }}>

          {loadingFirst && (
            <div className="articles-page-grid">
              {[1,2,3].map(i => (
                <div key={i} className="art-card" style={{ pointerEvents:'none', gridColumn: i===1?'span 3':'auto' }}>
                  <div className="art-img"><Skel style={{width:'100%',height:'100%'}}/></div>
                  <div className="art-body"><Skel cls="skel-tag"/><Skel cls="skel-line" style={{width:'80%',marginTop:'.5rem'}}/></div>
                </div>
              ))}
            </div>
          )}

          {!loadingFirst && articles.length === 0 && (
            <div style={{textAlign:'center',padding:'3rem',color:'var(--ink3)'}}>
              <div style={{fontSize:'2.5rem',marginBottom:'.75rem'}}>📝</div>
              <p>{NO_CONTENT_MSG[lang] || NO_CONTENT_MSG.ar}</p>
            </div>
          )}

          {!loadingFirst && articles.length > 0 && (
            <div className="articles-page-grid">
              {articles.map((a, i) => {
                const img = articleImg(a);
                const isFeatured = i === 0;
                const title = a.title || trunc(stripHtml(a.content), 80) || '';
                return (
                  <div key={a.id} className={`art-card${isFeatured?' featured':''}`} onClick={()=>navigate(`/articles/${a.id}`)} style={{cursor:'pointer'}}>
                    <div className="art-img" style={!img ? {
                      background: 'linear-gradient(135deg,var(--g1),var(--g2))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--fam-display)',
                      fontSize: isFeatured ? '5rem' : '4rem',
                      color: 'rgba(255,255,255,.3)'
                    } : {}}>
                      {img
                        ? <img src={img} alt={stripHtml(title)} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                        : <span>{title.charAt(0)}</span>
                      }
                    </div>
                    <div className="art-body">
                      <div className="art-cat">{a.category || t('label.article')}</div>
                      <a href={`/articles/${a.id}`} className="art-title" onClick={e=>e.preventDefault()}>
                        {stripHtml(title)}
                      </a>
                      <div className="art-exc">{trunc(a.content, isFeatured ? 160 : 100)}</div>
                      <div className="art-footer">
                        <span>{a.author || t('label.scholar')} · {articleDate(a, lang)}</span>
                        <span className="read">{isFeatured ? t('label.read.more') : t('label.read')}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SingleArticlePage({ article, scholars, prevArticle, nextArticle }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  if (!article) return null;

  const { title, content } = resolveVersion(article, lang);
  const displayTitle = title || trunc(stripHtml(article.content || ''), 80) || 'مقال';

  const scholar = scholars.find(s => s.name === article.author || (s.name && article.author && s.name.includes(article.author)));
  const idx = scholar ? scholars.indexOf(scholar) : 0;
  const bg = SCHOLAR_COLORS[idx % SCHOLAR_COLORS.length];
  const img = articleImg(article);

  let contentHtml = content || article.content || '';
  if (contentHtml && !contentHtml.includes('<')) {
    contentHtml = contentHtml.split(/\n+/).map(p=>p.trim()).filter(Boolean).map(p=>`<p>${p}</p>`).join('');
  }

  return (
    <div id="page-article-single">
      <div className="pw">
        <div style={{padding:'.8rem 0'}}>
          <a href="/articles" onClick={e=>{e.preventDefault();navigate('/articles');}} style={{color:'var(--g1)',fontWeight:700,fontSize:'var(--t-sm)'}}>
            {t('asin.back')}
          </a>
        </div>
        <div className="single-grid" style={{padding:'0 0 1.5rem'}}>
          <div className="single-body">
            <div className="single-header" style={img ? {
              backgroundImage:`linear-gradient(135deg,rgba(8,24,16,.92),rgba(27,67,50,.85)), url(${img})`,
              backgroundSize:'cover', backgroundPosition:'center'
            } : { background:'linear-gradient(135deg,#0a1f14,#1b4332)' }}>
              <div className="geo-sm"/>
              <div className="s-tag">{article.category || t('label.article')}</div>
              <h1>{stripHtml(displayTitle)}</h1>
              <div className="s-meta">
                <span style={{cursor:'pointer'}} onClick={()=>scholar&&navigate(`/scholars/${scholar.id}`)}>
                  👤 {article.author || t('label.scholar')}
                </span>
                <span>📅 {articleDate(article, lang)}</span>
                <span>⏱ {article.reading_time || t('asin.read.time')}</span>
              </div>
            </div>
            <div className="single-content">
              {(article.tags||[]).length > 0 && (
                <div style={{display:'flex',flexWrap:'wrap',gap:'.4rem',marginBottom:'1.2rem'}}>
                  {article.tags.map(tag=><span key={tag} className="ffc-tag">{tag}</span>)}
                </div>
              )}
              <div className="article-body-content" dangerouslySetInnerHTML={{__html:contentHtml}}/>
              <p style={{fontSize:'.8rem',color:'var(--ink4)',marginTop:'2rem',paddingTop:'1rem',borderTop:'1px dashed var(--brd)'}}>
                {t('asin.by')} {article.author||'المعهد'} · {(article.published_date||'').slice(0,10)} · {t('sf.institute')}
              </p>
            </div>
            <ShareButtons title={stripHtml(displayTitle)} />
            {(prevArticle || nextArticle) && (
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'stretch',gap:'1rem',margin:'2rem 0 0',borderTop:'1px solid var(--brd)',paddingTop:'1.5rem'}}>
                {prevArticle ? (
                  <button onClick={()=>navigate(`/articles/${prevArticle.id}`)} style={{flex:1,display:'flex',alignItems:'center',gap:'.75rem',padding:'.9rem 1.1rem',background:'var(--surface,#fff)',border:'1px solid var(--brd)',borderRadius:'10px',cursor:'pointer',textAlign:'left',transition:'border-color .2s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='var(--g1)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='var(--brd)'}>
                    <span style={{fontSize:'1.4rem',color:'var(--g1)'}}>←</span>
                    <span style={{display:'flex',flexDirection:'column',gap:'.2rem',overflow:'hidden'}}>
                      <span style={{fontSize:'.7rem',color:'var(--ink3)',textTransform:'uppercase',letterSpacing:'.05em'}}>{t('asin.prev')}</span>
                      <span style={{fontSize:'.85rem',fontWeight:600,color:'var(--ink)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{stripHtml(prevArticle.title||'')}</span>
                    </span>
                  </button>
                ) : <div style={{flex:1}}/>}
                {nextArticle ? (
                  <button onClick={()=>navigate(`/articles/${nextArticle.id}`)} style={{flex:1,display:'flex',alignItems:'center',justifyContent:'flex-end',gap:'.75rem',padding:'.9rem 1.1rem',background:'var(--surface,#fff)',border:'1px solid var(--brd)',borderRadius:'10px',cursor:'pointer',textAlign:'right',transition:'border-color .2s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='var(--g1)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='var(--brd)'}>
                    <span style={{display:'flex',flexDirection:'column',gap:'.2rem',overflow:'hidden'}}>
                      <span style={{fontSize:'.7rem',color:'var(--ink3)',textTransform:'uppercase',letterSpacing:'.05em'}}>{t('asin.next')}</span>
                      <span style={{fontSize:'.85rem',fontWeight:600,color:'var(--ink)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{stripHtml(nextArticle.title||'')}</span>
                    </span>
                    <span style={{fontSize:'1.4rem',color:'var(--g1)'}}>→</span>
                  </button>
                ) : <div style={{flex:1}}/>}
              </div>
            )}
          </div>
          <aside>
            <div className="single-sbox">
              <div className="ssb-head">{t('asin.author.box')}</div>
              <div style={{padding:'1.2rem',textAlign:'center'}}>
                <div style={{width:64,height:64,margin:'0 auto .8rem',background:bg,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'var(--fam-display)',fontSize:'1.8rem',color:'var(--a4)'}}>
                  {scholarInitial(article.author||'م')}
                </div>
                <div style={{fontFamily:'var(--fam-display)',fontSize:'1.05rem',color:'var(--ink)',marginBottom:'.3rem'}}>
                  {article.author||t('label.scholar')}
                </div>
                <button className="scf-btn" style={{width:'100%',marginTop:'.5rem'}} onClick={()=>scholar?navigate(`/scholars/${scholar.id}`):navigate('/scholars')}>
                  {t('sf.scholar.btn')} ←
                </button>
              </div>
            </div>
            <div className="hadith-widget" style={{marginBottom:'1.2rem'}}>
              <div className="geo-sm"/>
              <div className="title">{t('asin.ayah.title')}</div>
              <p>{t('asin.ayah.text2')}</p>
              <div className="ref">{t('asin.ayah.ref2')}</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}